import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Sheet } from "../../../design-v2";
import {
  CURRENT_ANALYSIS,
} from "../api/insightsApi";
import {
  CYCLE_ROLE,
  VOLUME_BASIS,
  WEEK_STATUS,
  buildMuscleGroups,
  buildTimelineColumns,
  formatSetCount,
  muscleLabel,
  readMuscleValue,
} from "../domain/insightsPresentation";
import InsightsLineChart from "./InsightsLineChart";

/*
 * Muscle Volume presentation. Planned and actual set values, node and parent
 * rows, all come from the API volume bodies (`week.planned`, `week.actual`):
 * this file picks rows and formats them. Contribution credit and parent
 * aggregation live in the backend engine only (§3, §4).
 */

export const MAX_CHART_MUSCLES = 3;
const SERIES_STYLE = [
  { tone: "action", marker: "circle" },
  { tone: "info", marker: "square" },
  { tone: "strong", marker: "diamond" },
];

export const BASIS_OPTIONS = [
  { value: VOLUME_BASIS.WEIGHTED, label: "Weighted" },
  { value: VOLUME_BASIS.DIRECT, label: "Direct" },
  { value: VOLUME_BASIS.INDIRECT, label: "Indirect" },
];

// Weeks with something performed or planned to read: an upcoming week has no
// actual value yet (shown as a gap, never as zero).
function actualFor(column, key, basis) {
  if (column.status === WEEK_STATUS.UPCOMING) return null;
  return readMuscleValue(column.week.actual, key, basis);
}

function plannedFor(column, key, basis) {
  return readMuscleValue(column.week.planned, key, basis);
}

// The default week: the in-progress week of the active cycle (Current
// Analysis) or the current calendar week (historical) — both API statuses.
export function defaultWeekIndex(columns, timeframe) {
  if (!columns.length) return -1;
  if (timeframe === CURRENT_ANALYSIS) {
    const current = columns.findIndex((column) => column.segment === CYCLE_ROLE.ACTIVE
      && column.status === WEEK_STATUS.IN_PROGRESS);
    if (current >= 0) return current;
    for (let index = columns.length - 1; index >= 0; index -= 1) {
      if (columns[index].status !== WEEK_STATUS.UPCOMING) return index;
    }
    return 0;
  }
  return columns.length - 1;
}

function firstBody(columns) {
  for (const column of columns) {
    const body = column.week.actual || column.week.planned;
    if (body) return body;
  }
  return null;
}

// The muscle groups with planned or logged weighted sets in one week, most
// planned first (then actual, then API order).
function rankWeekMuscles(groups, column) {
  const scored = groups.map((group, order) => ({
    key: group.key,
    order,
    planned: plannedFor(column, group.key, VOLUME_BASIS.WEIGHTED) || 0,
    actual: readMuscleValue(column.week.actual, group.key, VOLUME_BASIS.WEIGHTED) || 0,
  })).filter((entry) => entry.planned > 0 || entry.actual > 0);
  scored.sort((left, right) => right.planned - left.planned || right.actual - left.actual || left.order - right.order);
  return scored.map((entry) => entry.key);
}

// Weeks from the default one outwards: the default week, then its neighbours
// by distance, the earlier one first.
function weeksByDistance(columns, weekIndex) {
  const order = [];
  for (let distance = 0; distance < columns.length; distance += 1) {
    for (const index of distance ? [weekIndex - distance, weekIndex + distance] : [weekIndex]) {
      if (index >= 0 && index < columns.length) order.push(index);
    }
  }
  return order;
}

// Presentation choice only: which muscles to draw first. The muscle groups
// with the most planned weighted sets in the default week (then actual). When
// the default week has none, the nearest week of the period that has volume
// picks them, so a quiet current week never hides the rest of the period.
export function defaultChartMuscles(columns, weekIndex) {
  if (!columns[weekIndex]) return [];
  const groups = buildMuscleGroups(firstBody(columns));
  for (const index of weeksByDistance(columns, weekIndex)) {
    const ranked = rankWeekMuscles(groups, columns[index]);
    if (ranked.length) return ranked.slice(0, MAX_CHART_MUSCLES);
  }
  return [];
}

function bodyHasVolume(body) {
  return Boolean(body) && (body.nodes || []).some((row) => Object.values(VOLUME_BASIS)
    .some((basis) => Number(row[basis]) > 0));
}

// Whether any week of the response carries planned or logged volume, read
// from the API node rows as they are.
export function periodHasVolume(columns) {
  return columns.some((column) => bodyHasVolume(column.week.planned)
    || (column.status !== WEEK_STATUS.UPCOMING && bodyHasVolume(column.week.actual)));
}

export function useMuscleVolumeModel(response) {
  return useMemo(() => {
    const columns = buildTimelineColumns(response);
    const weekIndex = defaultWeekIndex(columns, response?.timeframe);
    return {
      columns,
      weekIndex,
      groups: buildMuscleGroups(firstBody(columns)),
      defaultMuscles: defaultChartMuscles(columns, weekIndex),
      hasVolume: periodHasVolume(columns),
    };
  }, [response]);
}

// legend="top" (detail screen): title with the Actual / Planned key beside it.
// legend="bottom" (Hub): no title; one key under the chart naming each charted
// muscle by its colour and marker, then Actual and Planned.
export function MuscleVolumeChart({ columns, muscles, basis, highlightIndex, title, hasVolume = false, legend = "top" }) {
  const series = muscles.flatMap((key, index) => {
    const style = SERIES_STYLE[index % SERIES_STYLE.length];
    return [
      {
        key: `${key}-planned`,
        name: `${muscleLabel(key)} planned`,
        tone: style.tone,
        secondary: true,
        values: columns.map((column) => plannedFor(column, key, basis)),
      },
      {
        key: `${key}-actual`,
        name: `${muscleLabel(key)} actual`,
        tone: style.tone,
        marker: style.marker,
        values: columns.map((column) => actualFor(column, key, basis)),
      },
    ];
  });
  const lineKey = (
    <>
      <li data-tone="strong"><span className="lz-insights-legend__swatch" />Actual</li>
      <li data-tone="muted"><span className="lz-insights-legend__swatch lz-insights-legend__swatch--secondary" />Planned</li>
    </>
  );
  return (
    <div className="lz-insights-volume-chart">
      {legend === "top" ? (
        <div className="lz-insights-sets__head">
          <h3 className="lz-insights-subtitle">{title}</h3>
          <ul className="lz-insights-legend lz-insights-legend--inline" aria-hidden="true">{lineKey}</ul>
        </div>
      ) : null}
      {muscles.length ? (
        <InsightsLineChart
          columns={columns}
          series={series}
          highlightIndex={highlightIndex}
          summary={`Sets per week, actual and planned: ${muscles.map(muscleLabel).join(", ")}`}
          formatValue={formatSetCount}
          xTitle="Weeks"
          yTitle="Sets"
          testId="muscle-volume-chart"
        />
      ) : (
        <p className="lz-insights-note">
          {hasVolume
            ? "Choose a muscle below to chart it."
            : "No muscle volume was planned or logged in this period."}
        </p>
      )}
      {legend === "bottom" && muscles.length ? (
        <ul className="lz-insights-legend" aria-hidden="true" data-testid="muscle-volume-legend">
          {muscles.map((key, index) => {
            const style = SERIES_STYLE[index % SERIES_STYLE.length];
            return (
              <li key={key} data-tone={style.tone}>
                <span className="lz-insights-legend__marker" data-shape={style.marker} />
                {muscleLabel(key)}
              </li>
            );
          })}
          {lineKey}
        </ul>
      ) : null}
    </div>
  );
}

// "10.5" + " / 12" (or no planned part when the week has no plan): actual
// over planned for one muscle in one week. An upcoming week has no actual.
function formatPair(actual, planned) {
  return {
    primary: actual === null ? "—" : formatSetCount(actual),
    planned: planned === null ? null : ` / ${formatSetCount(planned)}`,
  };
}

function SlotMarker({ shape }) {
  return <span className="lz-insights-chip__marker" data-shape={shape} aria-hidden="true" />;
}

/*
 * The chart's comparison: always three slots in one row. A charted muscle
 * shows its series marker and the selected week's actual over planned, as
 * plain facts (§21: no "below plan" / "on plan" judgement); × takes it off
 * the chart. A free slot is a quiet Add that opens the muscle sheet, as does
 * tapping a charted muscle (to swap it).
 */
export function MuscleSlots({ columns, muscles, basis, weekIndex, onRemove, onEdit }) {
  const column = columns[weekIndex];
  if (!column) return null;
  const free = Math.max(MAX_CHART_MUSCLES - muscles.length, 0);
  return (
    <ul className="lz-insights-slots" aria-label={`Charted muscles, sets in ${column.longLabel}`}>
      {muscles.map((key, index) => {
        const style = SERIES_STYLE[index % SERIES_STYLE.length];
        const label = muscleLabel(key);
        const pair = formatPair(actualFor(column, key, basis), plannedFor(column, key, basis));
        const planned = plannedFor(column, key, basis);
        return (
          <li key={key} className="lz-insights-slot" data-tone={style.tone} data-testid={`muscle-chip-${key}`}>
            <button type="button" className="lz-insights-slot__body" onClick={onEdit} aria-label={`Change ${label}`}>
              <span className="lz-insights-slot__name">{label}</span>
              <span className="lz-insights-slot__value">
                <SlotMarker shape={style.marker} />
                <strong>{pair.primary}</strong>
              </span>
              <span className="lz-insights-slot__meta">{planned === null ? "No plan" : `${formatSetCount(planned)} planned`}</span>
            </button>
            <button
              type="button"
              className="lz-insights-slot__remove"
              onClick={() => onRemove(key)}
              aria-label={`Remove ${label} from chart`}
            >
              <svg className="lz-insights-icon lz-insights-icon--sm" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 7l10 10M17 7L7 17" /></svg>
            </button>
          </li>
        );
      })}
      {Array.from({ length: free }, (_, index) => (
        <li key={`add-${index}`} className="lz-insights-slot lz-insights-slot--add">
          <button type="button" className="lz-insights-slot__body" onClick={onEdit} aria-label="Add muscle to chart">
            <svg className="lz-insights-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 6v12M6 12h12" /></svg>
            <span className="lz-insights-slot__add">Add</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export function WeekPager({ columns, weekIndex, onChange }) {
  const column = columns[weekIndex];
  if (!column) return null;
  return (
    <div className="lz-insights-pager" role="group" aria-label="Selected week">
      <button
        type="button"
        className="lz-insights-pager__button"
        onClick={() => onChange(weekIndex - 1)}
        disabled={weekIndex <= 0}
        aria-label="Previous week"
      >
        <svg className="lz-insights-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 6l-6 6 6 6" /></svg>
      </button>
      <p className="lz-insights-pager__label" aria-live="polite">
        <span className="lz-insights-pager__week">{column.label}</span>
      </p>
      <button
        type="button"
        className="lz-insights-pager__button"
        onClick={() => onChange(weekIndex + 1)}
        disabled={weekIndex >= columns.length - 1}
        aria-label="Next week"
      >
        <svg className="lz-insights-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 6l6 6-6 6" /></svg>
      </button>
    </div>
  );
}

const SHEET_EXIT_MS = 180;

function sheetMotionAllowed() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function MuscleValue({ muscleKey, column, basis }) {
  const pair = formatPair(actualFor(column, muscleKey, basis), plannedFor(column, muscleKey, basis));
  return (
    <span className="lz-insights-muscle__value">
      <strong>{pair.primary}</strong>
      <span>{pair.planned ?? " · no plan"}</span>
    </span>
  );
}

// One sheet row: a canonical muscle (group, standalone node or a group's
// child node) the chart can draw, with the week's actual / planned sets.
function PickRow({ muscleKey, column, basis, selected, full, onToggle, child = false }) {
  const label = muscleLabel(muscleKey);
  const position = selected.indexOf(muscleKey);
  const isOn = position >= 0;
  const style = isOn ? SERIES_STYLE[position % SERIES_STYLE.length] : null;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={isOn}
      aria-label={label}
      disabled={!isOn && full}
      className={`lz-insights-pick${child ? " lz-insights-pick--child" : ""}`}
      data-tone={style?.tone}
      data-testid={`muscle-row-${muscleKey}`}
      onClick={() => onToggle(muscleKey)}
    >
      <span className="lz-insights-pick__check" aria-hidden="true">
        <svg className="lz-insights-icon lz-insights-icon--sm" viewBox="0 0 24 24" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
      </span>
      <span className="lz-insights-pick__name">
        {style ? <SlotMarker shape={style.marker} /> : null}
        <span>{label}</span>
      </span>
      <MuscleValue muscleKey={muscleKey} column={column} basis={basis} />
    </button>
  );
}

/*
 * The muscle sheet: every muscle group of the period in the API's canonical
 * order, with the selected week's actual / planned sets. Groups and their
 * child contribution nodes are all chartable, a group together with its own
 * muscles too: three at most (the rest wait, disabled, while three are
 * charted), and the sheet stays open until Done. Children sit indented under
 * their group with their own API rows (§3). Emphasis tags (`emphasis[]`) are
 * not set-credit nodes and are never listed.
 */
export function MuscleSheet({ open, onClose, groups, columns, basis, weekIndex, selected, onToggle }) {
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef(null);
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  const column = columns[weekIndex];
  if (!column) return null;
  const full = selected.length >= MAX_CHART_MUSCLES;

  const requestClose = () => {
    if (closing) return;
    if (!sheetMotionAllowed()) {
      onClose();
      return;
    }
    setClosing(true);
    closeTimer.current = setTimeout(() => {
      setClosing(false);
      onClose();
    }, SHEET_EXIT_MS);
  };

  return (
    <Sheet
      open={open}
      onClose={requestClose}
      title="Select muscles"
      closeLabel="Close muscle selection"
      description={(
        <>
          <span data-testid="muscle-sheet-count">
            <strong className="lz-insights-sheet__count">{selected.length}</strong> of {MAX_CHART_MUSCLES} selected
          </span>
          <span>{column.label}</span>
        </>
      )}
      className={`lz-insights-sheet-overlay${closing ? " is-closing" : ""}`}
      surfaceClassName={`lz-insights-sheet${closing ? " is-closing" : ""}`}
      footer={<Button className="lz-insights-sheet__done" onClick={requestClose}>Done</Button>}
    >
      <ul className="lz-insights-picks" aria-label={`Muscles, sets in ${column.longLabel}`}>
        {groups.map((group) => {
          const rowProps = { column, basis, selected, full, onToggle };
          return (
            <li key={group.key}>
              <PickRow muscleKey={group.key} {...rowProps} />
              {group.kind === "parent" ? (
                <ul className="lz-insights-picks__children" aria-label={`${muscleLabel(group.key)} muscles`}>
                  {group.children.map((child) => (
                    <li key={child}>
                      <PickRow muscleKey={child} {...rowProps} child />
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Sheet>
  );
}
